import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LogOut } from "lucide-react";
import { removeAuthToken } from "../../utils/auth";
import { ChevronLeft } from "lucide-react";
import { updateUserPassword } from "../../services/apiService";

const ChangePassword = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  useEffect(() => {
    if (!email) {
      navigate("/");
    }
  }, [email, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("email"); 
    removeAuthToken();
    navigate("/", { replace: true });
  };

  const validatePassword = (password) => {
    setPasswordChecks({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handleNewPasswordChange = (e) => {
    let password = e.target.value;
    setNewPassword(password);
    validatePassword(password);
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const doPasswordsMatch = newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!isPasswordValid) {
      setMessage("Password must be at least 8 characters and meet all requirements.");
      setLoading(false);
      return;
    }

    if (!doPasswordsMatch) {
      setMessage("New password and confirm password do not match.");
      setLoading(false);
      return;
    }

    try {
      const response = await updateUserPassword(oldPassword, newPassword, email);
      setMessage(response.message || "Password updated successfully!");
      navigate("/dashboard");
    } catch (error) {
      setMessage(error.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
            <div className="flex items-center mb-8">
              <button className="p-2 -ml-2" onClick={() => navigate(`/profile/${email}`)}>
                <ChevronLeft className="h-6 w-6" />
              </button>
              <h1 className="text-xl font-semibold ml-2">Password Update</h1>
            </div>

      <div className="flex flex-col justify-center flex-1 px-6">
        {message && <p className="text-center text-red-500 mb-4">{message}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium text-gray-300">Old Password</label>
            <input
              type="password"
              className="w-full px-4 py-2 mt-1 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block font-medium text-gray-300">New Password</label>
            <input
              type="password"
              className="w-full px-4 py-2 mt-1 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              value={newPassword}
              onChange={handleNewPasswordChange}
              required
            />
            <div className="mt-2 text-sm">
              <p className="flex items-center">
                {passwordChecks.length ? "✅" : "❌"} At least 8 characters required
              </p>
              <p className="flex items-center">
                {passwordChecks.uppercase ? "✅" : "❌"} At least 1 uppercase letter
              </p>
              <p className="flex items-center">
                {passwordChecks.lowercase ? "✅" : "❌"} At least 1 lowercase letter
              </p>
              <p className="flex items-center">
                {passwordChecks.number ? "✅" : "❌"} At least 1 number
              </p>
              <p className="flex items-center">
                {passwordChecks.specialChar ? "✅" : "❌"} At least 1 special character (!@#$%^&*)
              </p>
            </div>
          </div>

          <div>
            <label className="block font-medium text-gray-300">Confirm Password</label>
            <input
              type="password"
              className={`w-full px-4 py-2 mt-1 bg-gray-900 border rounded-lg focus:outline-none focus:ring-2 text-white ${
                confirmPassword && !doPasswordsMatch ? "border-red-500 focus:ring-red-500" : "border-gray-700 focus:ring-blue-500"
              }`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            {confirmPassword && !doPasswordsMatch && (
              <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
            disabled={loading || !isPasswordValid || !doPasswordsMatch}
          >
            {loading ? "Updating..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
